import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faCheck, faXmark } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Card, Grid, Chip, Tooltip } from '@mui/material'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useExportEvents } from '@/api/campaigns/campaign-export-events'
import { useCampaign } from '@/api/campaigns/get'
import { useCampaignMembersInteractions } from '@/api/campaigns/getInteractions'
import LayoutPageContent from '@/common/components/Layout/LayoutPageContent'
import PageHeader from '@/common/components/Layout/LayoutPageContent/PageHeader'
import SimpleSearchBar from '@/common/components/SimpleSearchBar/SimpleSearchBar'
import Table, { TalbeColumn } from '@/common/components/Table'
import useQueryParams from '@/common/hooks/useQueryParams'
import { theme } from '@/theme/theme'
import { AssetType } from '@/types/campaigns'
import MultiSelectFilter from '@/common/components/MultiSelectFilter'

const MembersInteractions = () => {
  const { _id = '' } = useParams()
  const { t } = useTranslation()

  const { data: campaignData } = useCampaign(_id)

  const { mutateAsync: exportEvents } = useExportEvents()

  const eventsOptions = useMemo(
    () =>
      campaignData?.asset_type === AssetType.phishing_simulation
        ? [
            { value: 'email_open', label: 'Opened', translationKey: 'opened' },
            { value: 'email_link_click', label: 'Clicked', translationKey: 'clicked' },
            // The value below is for submission, not inserted data. But displayed as inserted_data.
            // TODO: align event types
            { value: 'submit', label: 'Inserted', translationKey: 'inserted' },
            //   { value: 'submit', label: 'Submit' },
            { value: 'feedback_completed', label: 'Micro Training', translationKey: 'microTraining' },
            { value: 'report', label: 'Reported', translationKey: 'reported' },
          ]
        : [
            { value: 'training_completed', label: 'Videos Completed', translationKey: 'videosCompleted' },
            { value: 'quiz_completed', label: 'Quizzes Completed', translationKey: 'quizzesCompleted' },
          ],
    [campaignData]
  )
  const INITIAL_QUERY_FILTERS: any = {
    search: '',
    event_type: [],
    skip: 0,
    limit: 10,
  }
  const { queryParams, updateQueryParam } = useQueryParams(INITIAL_QUERY_FILTERS)
  const { data: membersInteractions, isFetching } = useCampaignMembersInteractions(_id, queryParams)
  const { results: membersInteractionsData, total } = membersInteractions ?? { total: 0, results: [] }

  const tableColumns: TalbeColumn[] = [
    {
      id: 'full_name',
      label: t('campaignDashboard.campaignInteraction.fullName'),
      sortable: true,
    },
    {
      id: 'username',
      label: t('campaignDashboard.campaignInteraction.email'),
      sortable: true,
    },
    {
      id: 'office',
      label: t('campaignDashboard.campaignInteraction.office'),
      sortable: true,
    },
    {
      id: 'groups',
      label: t('campaignDashboard.campaignInteraction.groups'),
      sortable: true,
      component: (row: any) => {
        if (row.groups.length === 0) return 'N/A'
        if (row.groups.length === 1) return row.groups[0]
        return (
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            {row.groups[0]}
            <Tooltip title={row.groups.slice(1).join(', ')} placement="top">
              <Chip size="small" label={`+${row.groups.length - 1}`} variant="outlined" />
            </Tooltip>
          </Box>
        )
      },
    },
    ...eventsOptions.map((event) => ({
      id: event.value,
      label: t(`campaignDashboard.campaignInteraction.${event.translationKey}`),
      component: (row: any) =>
        row.events[event.value] ? (
          <FontAwesomeIcon icon={faCheck as IconProp} fontSize={20} color={theme.palette.green[500]} />
        ) : event.value === 'quiz_completed' && !campaignData?.asset_metadata?.include_quiz ? (
          'N/A'
        ) : (
          <FontAwesomeIcon icon={faXmark as IconProp} fontSize={20} color={theme.palette.red[500]} />
        ),
    })),
  ]

  const handleSearch = useCallback((value: string) => {
    updateQueryParam({ search: value })
  }, [])

  return (
    <LayoutPageContent>
      <PageHeader>
        <Button
          variant="contained"
          size="medium"
          disabled={!campaignData?.events.email_open}
          onClick={() => exportEvents(_id)}>
          {t('campaignDashboard.downloadEvents')}
        </Button>
      </PageHeader>
      <Grid item xs={12}>
        <Card>
          <Box display="flex" gap={4} my={1}>
            <SimpleSearchBar onChange={handleSearch} />
            <MultiSelectFilter
              options={eventsOptions}
              value={queryParams.event_type}
              label={t('Interactions')}
              onChange={(value) => updateQueryParam({ event_type: value })}
              width={400}
            />
          </Box>
          <Table
            loading={isFetching}
            data={membersInteractionsData}
            total={total}
            queryParams={queryParams}
            updateQueryParam={updateQueryParam}
            columns={tableColumns}
          />
        </Card>
      </Grid>
    </LayoutPageContent>
  )
}

export default MembersInteractions
